import { FormControl, FormLabel, Switch } from '@chakra-ui/react';
import { ArticleNodeGroup } from '@texas/api/endpoints/articlesApi';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { ReactSelectOption } from '@texas/types';
import { useEffect } from 'react';
import { useVersionOverviewContext } from './VersionOverviewContext';
import { useActiveSession } from '@texas/hooks/useSession';
import { useTranslation } from 'react-i18next';
import {
  articleContextEvents,
  ArticleContextValue,
} from '@texas/components/article/articleContext/articleContextEvents';

interface Props {
  variantId: number;
  articleNodes?: ArticleNodeGroup;
  supplierFilterOptions?: Array<{ id: number; name: string }>;
  optionFilterOptions?: Array<{
    id: number;
    version: number;
    note: string | null;
  }>;
  hideBranchFilter?: boolean;
  hideMatrixFilters?: boolean;
  hideArchivedFilter?: boolean;
  compact?: boolean;
  initialBranchId?: number;
}

export function VersionsFilters({
  variantId,
  articleNodes,
  supplierFilterOptions,
  optionFilterOptions,
  hideBranchFilter,
  hideMatrixFilters,
  hideArchivedFilter,
  compact,
  initialBranchId,
}: Props) {
  const { filters, updateFilter } = useVersionOverviewContext();
  const { currentUser } = useActiveSession();
  const { t } = useTranslation();

  const {
    data: suppliers,
    refetch: refetchSuppliers,
    loading: loadingSuppliers,
  } = useApiResource(variantsApi.getVariantBranchSuppliers);

  const {
    data: options,
    refetch: refetchOptions,
    loading: loadingOptions,
  } = useApiResource(compositionGroupApi.optionsOverview);

  const { data: productDevelopers, refetch: refetchProductDevelopers } =
    useApiResource(variantsApi.getVariantProductDevelopers);

  useEffect(() => {
    if (!supplierFilterOptions) refetchSuppliers(variantId, false);
    if (!optionFilterOptions) refetchOptions({ variantId });
  }, [
    optionFilterOptions,
    refetchSuppliers,
    refetchOptions,
    supplierFilterOptions,
    variantId,
  ]);

  useEffect(() => {
    if (hideBranchFilter) return;
    refetchProductDevelopers(variantId);
  }, [hideBranchFilter, variantId, refetchProductDevelopers]);

  // Preselect branch if user is product developer
  useEffect(() => {
    if (initialBranchId) return;
    if (productDevelopers && !hideBranchFilter) {
      const userBranches = productDevelopers.filter(
        (pd) => pd.userId === currentUser.id,
      );
      if (userBranches.length > 0) {
        updateFilter('branchIds', [userBranches.map((b) => b.branchId)].flat());
      }
    }
  }, [
    productDevelopers,
    currentUser.id,
    hideBranchFilter,
    initialBranchId,
    updateFilter,
  ]);

  useEffect(() => {
    const syncBranchScope = (context: ArticleContextValue) => {
      if (context.variantId !== variantId) return;
      updateFilter(
        'branchIds',
        context.branchIds.length > 0 ? context.branchIds : undefined,
      );
    };

    if (!initialBranchId) syncBranchScope(articleContextEvents.current);
    return articleContextEvents.contextChanged.subscribe(syncBranchScope);
  }, [initialBranchId, updateFilter, variantId]);

  const uniqueSupplierOptions = Array.from(
    new Map(
      (supplierFilterOptions ?? suppliers?.suppliers ?? []).map((supplier) => {
        const id = 'supplierId' in supplier ? supplier.supplierId : supplier.id;
        const name =
          'supplierName' in supplier ? supplier.supplierName : supplier.name;
        return [id, { value: id, label: name }];
      }),
    ).values(),
  );

  const branches = suppliers?.branches ?? [];

  const columnOptions: ReactSelectOption[] =
    articleNodes?.nodeXValues.map((x) => ({
      value: x.matrixNodeValue.id,
      label: x.name ?? x.matrixNodeValue.name ?? x.matrixNodeValue.identifier,
    })) ?? [];

  const rowOptions: ReactSelectOption[] =
    articleNodes?.nodeYValues.map((y) => ({
      value: y.matrixNodeValue.id,
      label: y.name ?? y.matrixNodeValue.name ?? y.matrixNodeValue.identifier,
    })) ?? [];

  const formLabelProps = compact
    ? {
        position: 'absolute' as const,
        width: '1px',
        height: '1px',
        overflow: 'hidden',
      }
    : { fontSize: 'sm' };
  const controlProps = {
    maxW: '300px',
    ...(compact ? { flexShrink: 0 } : {}),
  };

  return (
    <>
      <FormControl {...controlProps}>
        <FormLabel {...formLabelProps}>{t('general.suppliers')}</FormLabel>
        <TexasSelect
          borderRadius={compact ? 0 : undefined}
          isLoading={!supplierFilterOptions && loadingSuppliers}
          isClearable={true}
          isMulti={true}
          minHeight={compact ? '36px' : undefined}
          closeMenuOnSelect={false}
          placeholder={t('suppliers.all')}
          value={
            filters.supplierIds?.map((id) => {
              const supplier = uniqueSupplierOptions.find(
                (option) => option.value === id,
              );
              return {
                value: id,
                label: supplier?.label ?? '',
              };
            }) ?? []
          }
          onChange={(selected) => {
            const selectedIds = selected.map((s) => s.value);
            updateFilter(
              'supplierIds',
              selectedIds.length > 0 ? selectedIds : undefined,
            );
          }}
          options={uniqueSupplierOptions}
        />
      </FormControl>

      {!hideBranchFilter && (
        <FormControl {...controlProps}>
          <FormLabel {...formLabelProps}>{t('general.branches')}</FormLabel>
          <TexasSelect
            borderRadius={compact ? 0 : undefined}
            isLoading={loadingSuppliers}
            isClearable={true}
            isMulti={true}
            minHeight={compact ? '36px' : undefined}
            closeMenuOnSelect={false}
            placeholder={t('filter.allBranches')}
            value={
              filters.branchIds?.map<ReactSelectOption>((id) => {
                const branch = branches.find((b) => b.id === id);
                return {
                  label: branch ? `${branch.name} (${branch.identifier})` : '',
                  value: id,
                };
              }) ?? []
            }
            onChange={(selected) => {
              const selectedIds = selected.map((s) => s.value);
              updateFilter(
                'branchIds',
                selectedIds.length > 0 ? selectedIds : undefined,
              );
            }}
            options={branches.map<ReactSelectOption>((b) => ({
              value: b.id,
              label: `${b.name} (${b.identifier})`,
            }))}
          />
        </FormControl>
      )}

      <FormControl {...controlProps}>
        <FormLabel {...formLabelProps}>{t('general.options')}</FormLabel>
        <TexasSelect
          borderRadius={compact ? 0 : undefined}
          isLoading={!optionFilterOptions && loadingOptions}
          isClearable={true}
          isMulti={true}
          minHeight={compact ? '36px' : undefined}
          closeMenuOnSelect={false}
          placeholder={t('filter.allOptions')}
          value={
            filters.optionIds?.map<ReactSelectOption>((id) => {
              const option = (optionFilterOptions ?? options)?.find(
                (item) => item.id === id,
              );
              return {
                label: option?.note ?? `Option ${option?.version ?? id}`,
                value: id,
              };
            }) ?? []
          }
          onChange={(selected) => {
            const selectedIds = selected.map((s) => s.value);
            updateFilter(
              'optionIds',
              selectedIds.length > 0 ? selectedIds : undefined,
            );
          }}
          options={
            (optionFilterOptions ?? options)?.map<ReactSelectOption>((o) => ({
              value: o.id,
              label: o.note ?? `Option ${o.version}`,
            })) ?? []
          }
        />
      </FormControl>

      {articleNodes && !hideMatrixFilters && (
        <FormControl maxW="200px">
          <FormLabel fontSize="sm">{t('filter.columns')}</FormLabel>
          <TexasSelect
            isClearable={true}
            placeholder={t('filter.all')}
            value={
              filters.cellX !== undefined
                ? columnOptions.find((o) => o.value === filters.cellX) ?? null
                : null
            }
            onChange={(selected) => {
              updateFilter('cellX', selected ? selected.value : undefined);
            }}
            options={columnOptions}
          />
        </FormControl>
      )}

      {articleNodes && !hideMatrixFilters && (
        <FormControl maxW="200px">
          <FormLabel fontSize="sm">{t('filter.rows')}</FormLabel>
          <TexasSelect
            isClearable={true}
            placeholder={t('filter.all')}
            value={
              filters.cellY !== undefined
                ? rowOptions.find((o) => o.value === filters.cellY) ?? null
                : null
            }
            onChange={(selected) => {
              updateFilter('cellY', selected ? selected.value : undefined);
            }}
            options={rowOptions}
          />
        </FormControl>
      )}

      {!hideArchivedFilter && (
        <FormControl w="fit-content">
          <FormLabel fontSize="sm" mb={1}>
            {t('compositionGroup.version.showArchived')}
          </FormLabel>
          <Switch
            isChecked={filters.showArchived ?? false}
            onChange={(e) => updateFilter('showArchived', e.target.checked)}
          />
        </FormControl>
      )}
    </>
  );
}
